import HoursDisplay from 'components/util/HoursDisplay';
import MoneyDisplay from 'components/util/MoneyDisplay';
import { format, isSameDay } from 'date-fns';
import EffortListItem from 'pages/efforts/dashboard/EffortListItem';
import LabelledData from 'pages/timecards/LabelledData';
import TimecardListItem from 'pages/timecards/TimecardListItem';
import { FC } from 'react';
import { ClassifierData } from 'types/classifiers';
import { EffortListItem as EffortListItemData } from 'types/efforts';
import { TimecardListItemData } from 'types/timecards';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { dateStringToDateObject } from 'utils/parseDate';

type Props = {
    date: string;
    timecards: TimecardListItemData[];
    efforts: EffortListItemData[];
    classifier: ClassifierData;
    classifiers: Record<string, ClassifierData>;
};

const ROW = 'relative flex w-full items-center';

const formatGroupDate = (date: string) => {
    const dateObj = dateStringToDateObject(date);
    const isToday = isSameDay(dateObj, new Date());
    return isToday
        ? `${t('time.today')}, ${format(dateObj, t('timecards.list.group-date-format-without-day'))}`
        : format(dateObj, t('timecards.list.group-date-format'));
};

/**
 * One card per day combining the day's timecards (drafts + finalized, in BE order) and BD efforts
 * underneath a single date header.
 */
const TimeRecordDayCard: FC<Props> = ({ date, timecards, efforts, classifier, classifiers }) => {
    const loggedHours =
        timecards.reduce((sum, tc) => sum + (tc.timecardStats?.totalTimeInHours ?? 0), 0) +
        efforts.reduce((sum, e) => sum + e.usedTimeInSeconds / 3600, 0);
    const billableHours = timecards.reduce((sum, tc) => sum + (tc.timecardStats?.billedTimeInHours ?? 0), 0);
    const totalAmountInBaseCurrency = timecards.reduce(
        (sum, tc) => sum + (tc.isNonBillable ? 0 : (tc.currencyDetails?.totalAmountInBaseCurrency ?? 0)),
        0
    );
    const baseCurrency = timecards.find((tc) => tc.currencyDetails?.baseCurrency)?.currencyDetails?.baseCurrency ?? '';

    const total = timecards.length + efforts.length;

    return (
        <div className="bg-paper border-divider overflow-hidden border-t">
            {/* The 24px line height is the header row's strut: the labelled figures sit on it. */}
            <div className="bg-grey-50 border-divider flex flex-wrap items-baseline justify-between border-b px-4 py-[9px] leading-6">
                <h6 className="text-subtitle2">{formatGroupDate(date)}</h6>
                <div className="flex gap-2">
                    <LabelledData label="timecards.list.total-time">
                        <span className="font-semibold">
                            <HoursDisplay value={loggedHours} />
                        </span>
                    </LabelledData>
                    {timecards.length > 0 && (
                        <>
                            <LabelledData label="timecards.list.billable-time">
                                <span className="font-semibold">
                                    <HoursDisplay value={billableHours} />
                                </span>
                            </LabelledData>
                            <LabelledData label="timecards.list.total-amount">
                                <span className="font-semibold">
                                    <MoneyDisplay amount={totalAmountInBaseCurrency} currency={baseCurrency} />
                                </span>
                            </LabelledData>
                        </>
                    )}
                </div>
            </div>
            <ul>
                {timecards.map((timecard, index) => (
                    <li key={`tc-${timecard.id}`} className={cn(ROW, index !== total - 1 && 'border-divider border-b')}>
                        <TimecardListItem
                            timecard={timecard}
                            classifier={classifier}
                            classifiers={classifiers}
                            compact
                        />
                    </li>
                ))}
                {efforts.map((effort, index) => (
                    <li
                        key={`ef-${effort.id}`}
                        className={cn(ROW, index !== efforts.length - 1 && 'border-divider border-b')}
                    >
                        <EffortListItem effort={effort} />
                    </li>
                ))}
            </ul>
        </div>
    );
};

export default TimeRecordDayCard;
